import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useSubscription } from '../hooks/useSubscription';
import { T } from '../styles/theme';
import { useIsMobile } from '../hooks/useIsMobile';

const Tip = ({ children }) => (
  <div style={{ background: 'rgba(251,191,36,0.08)', borderLeft: '3px solid #fbbf24', borderRadius: '0 8px 8px 0', padding: '12px 16px', margin: '12px 0', color: '#fbbf24', fontSize: '0.88rem', lineHeight: 1.6 }}>
    {children}
  </div>
);
const Warn = ({ children }) => (
  <div style={{ background: 'rgba(248,113,113,0.08)', borderLeft: '3px solid #f87171', borderRadius: '0 8px 8px 0', padding: '12px 16px', margin: '12px 0', color: '#f87171', fontSize: '0.88rem', lineHeight: 1.6 }}>
    {children}
  </div>
);
const Step = ({ n, children }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '8px 0' }}>
    <span style={{ minWidth: 24, height: 24, borderRadius: '50%', background: '#7c3aed', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800, flexShrink: 0, marginTop: 2 }}>{n}</span>
    <span style={{ color: '#9ca3af', lineHeight: 1.7, fontSize: '0.92rem' }}>{children}</span>
  </div>
);
const GoBtn = ({ to, label }) => {
  const nav = useNavigate();
  return <button onClick={() => nav(to)} style={{ marginTop: 8, padding: '6px 16px', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', border: 'none', borderRadius: 9999, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>{label}</button>;
};
const ProLock = ({ feature }) => {
  const nav = useNavigate();
  return (
    <div style={{ position: 'relative', borderRadius: 12, marginTop: 16, minHeight: 160 }}>
      <div style={{ filter: 'blur(4px)', opacity: 0.15, pointerEvents: 'none' }}>
        <p style={{ color: '#9ca3af', lineHeight: 1.7, fontSize: '0.92rem' }}>Contenuto dettagliato disponibile con il piano PRO. Sblocca tutte le guide avanzate per portare il tuo business al livello successivo.</p>
        <p style={{ color: '#9ca3af', lineHeight: 1.7, fontSize: '0.92rem' }}>Tutorial step-by-step, consigli pratici e strategie avanzate per massimizzare i tuoi profitti.</p>
      </div>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', background: 'rgba(13,11,30,0.97)', border: '1px solid #7c3aed', borderRadius: 16, padding: '24px 32px', boxShadow: '0 0 30px rgba(124,58,237,0.2)' }}>
          <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: 8 }}>🔒</span>
          <p style={{ fontSize: '0.88rem', color: '#a78bfa', fontWeight: 700, margin: '0 0 12px' }}>Disponibile con piano PRO</p>
          <button onClick={() => nav('/abbonamento')} style={{ padding: '8px 20px', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', border: 'none', borderRadius: 9999, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(124,58,237,0.4)' }}>Upgrade a PRO</button>
        </div>
      </div>
    </div>
  );
};

const SECTIONS = [
  { id: 'video', title: '📺 Video Tutorial', pro: false, badge: 'PRO' },
  { id: 'benvenuto', title: '👋 Benvenuto', pro: false },
  { id: 'smartphone', title: '📱 Smartphone', pro: false },
  { id: 'inventario', title: '📦 Inventario', pro: false },
  { id: 'vendite', title: '💰 Vendite', pro: false },
  { id: 'corso', title: '🎓 Corso', pro: false },
  { id: 'fornitori', title: '🤝 Fornitori', pro: false },
  { id: 'annunci', title: '🤖 Annunci AI', pro: true },
  { id: 'analisi', title: '📊 Analisi Mercato', pro: true },
  { id: 'bozze', title: '✏️ Bozze & Editor', pro: true },
  { id: 'chatbot', title: '💬 Chatbot AI', pro: true },
  { id: 'estensione', title: '🔧 Estensione Chrome', pro: true },
];

export default function Guida() {
  const { isPro } = useSubscription();
  const isMobile = useIsMobile();
  const nav = useNavigate();
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState('benvenuto');
  const [showTop, setShowTop] = useState(false);
  const [videoStart, setVideoStart] = useState(0);
  const sectionRefs = useRef({});

  const CAPITOLI = [
    { tempo: "0:00", secondi: 0, titolo: "Introduzione", icona: "👋" },
    { tempo: "1:39", secondi: 99, titolo: "Dashboard", icona: "🏠" },
    { tempo: "3:20", secondi: 200, titolo: "Inventario", icona: "📦" },
    { tempo: "5:07", secondi: 307, titolo: "Analisi Mercato", icona: "📊" },
    { tempo: "7:23", secondi: 443, titolo: "Bot Vinted", icona: "👗" },
    { tempo: "13:05", secondi: 785, titolo: "Bot eBay", icona: "🛒" },
    { tempo: "15:41", secondi: 941, titolo: "Gestione Vendite", icona: "💰" },
    { tempo: "19:18", secondi: 1158, titolo: "Estensione Vinted", icona: "🔧" },
    { tempo: "30:30", secondi: 1830, titolo: "Fine", icona: "🏁" },
  ];

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  const filtered = search ? SECTIONS.filter(s => s.title.toLowerCase().includes(search.toLowerCase())) : SECTIONS;

  const P = ({ children }) => <p style={{ color: '#9ca3af', lineHeight: 1.7, fontSize: '0.92rem', marginTop: 8 }}>{children}</p>;
  const H3 = ({ children }) => <h3 style={{ color: '#e2e8f0', fontSize: '1rem', fontWeight: 600, marginTop: 24, marginBottom: 4 }}>{children}</h3>;

  const renderSection = (id) => {
    const isProSection = SECTIONS.find(s => s.id === id)?.pro;
    if (isProSection && !isPro) return <ProLock feature={id} />;

    switch (id) {
      case 'video': return (<>
        <p style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: 16, lineHeight: 1.6 }}>Questo tutorial di 35 minuti ti guida attraverso tutte le funzionalità della Dashboard e dell'Estensione Chrome. Ideale per iniziare subito nel modo giusto.</p>

        {/* Player con overlay FREE */}
        <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
          <iframe
            width="100%"
            style={{ aspectRatio: '16/9', border: 'none', display: 'block', filter: isPro ? 'none' : 'blur(8px)' }}
            src={`https://www.youtube.com/embed/6GFFQEmAQCc?start=${videoStart}`}
            title="Tutorial Completo Impero Elite Dashboard"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen={isPro}
          />
          {!isPro && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,11,30,0.6)' }}>
              <div style={{ textAlign: 'center', background: 'rgba(13,11,30,0.95)', border: '1px solid #7c3aed', borderRadius: 16, padding: '28px 36px', boxShadow: '0 0 30px rgba(124,58,237,0.2)' }}>
                <span style={{ fontSize: '2rem', display: 'block', marginBottom: 10 }}>🔒</span>
                <p style={{ color: '#e2e8f0', fontSize: '0.95rem', fontWeight: 600, margin: '0 0 6px' }}>Tutorial completo disponibile</p>
                <p style={{ color: '#9ca3af', fontSize: '0.82rem', margin: '0 0 16px' }}>con il Piano PRO</p>
                <button onClick={() => nav('/abbonamento')} style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', border: 'none', borderRadius: 9999, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(124,58,237,0.4)' }}>⚡ Upgrade a PRO</button>
              </div>
            </div>
          )}
        </div>

        {/* Capitoli */}
        <h3 style={{ color: '#e2e8f0', fontSize: '1rem', fontWeight: 600, marginTop: 24, marginBottom: 4 }}>📋 Capitoli del tutorial</h3>
        <p style={{ color: '#6b7280', fontSize: '0.82rem', marginBottom: 12, lineHeight: 1.5 }}>Clicca su un capitolo per andare direttamente al minuto esatto nel video</p>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
          {CAPITOLI.map((c, i) => (
            <button key={i} onClick={() => {
              if (isPro) { setVideoStart(c.secondi); sectionRefs.current['video']?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
              else { alert('Disponibile con Piano PRO'); }
            }} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#160d30', border: '1px solid transparent', borderRadius: 8,
              cursor: 'pointer', transition: '0.15s', textAlign: 'left', width: '100%',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1a1035'; e.currentTarget.style.borderColor = '#2d1b69'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#160d30'; e.currentTarget.style.borderColor = 'transparent'; }}
            >
              <span style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: 6, fontFamily: 'JetBrains Mono,monospace', flexShrink: 0 }}>{c.tempo}</span>
              <span style={{ fontSize: '0.85rem', flexShrink: 0 }}>{c.icona}</span>
              <span style={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: 500, flex: 1 }}>{c.titolo}</span>
              <span style={{ color: '#6b7280', fontSize: '0.75rem', flexShrink: 0 }}>{isPro ? '→' : '🔒'}</span>
            </button>
          ))}
        </div>

        <p style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 16, lineHeight: 1.6 }}>💡 Puoi consultare questo video in qualsiasi momento dalla sezione Guida.</p>
      </>);
      case 'benvenuto': return (<>
        <H3>Cos'e la Dashboard e a cosa serve</H3>
        <P>Impero Elite Dashboard e il tuo strumento professionale per gestire tutto il business di reselling in un unico posto: inventario, vendite, annunci, analisi prezzi e corso formativo. Senza Dashboard devi tenere tutto su fogli Excel, scrivere gli annunci a mano e non hai idea se stai vendendo al prezzo giusto.</P>
        <H3>Come navigare l'interfaccia</H3>
        <P>Nella sidebar a sinistra trovi tutte le sezioni: Dashboard (panoramica), Inventario (i tuoi prodotti), Annunci (genera con AI), Bozze (prepara e pubblica), Analisi Mercato (trova il prezzo giusto), Fornitori (contatti all'ingrosso), Vendite (traccia i profitti), Corso (impara a vendere). Su mobile: clicca il menu hamburger in alto a sinistra.</P>
        <H3>Piano FREE vs Piano PRO</H3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div style={{ background: '#151230', border: '1px solid #2d1b69', borderRadius: 10, padding: 14 }}>
            <p style={{ color: '#9ca3af', fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>FREE</p>
            {['Dashboard base', 'Inventario manuale', 'Corso completo', 'Vendite', 'Fornitori'].map(f => <p key={f} style={{ color: '#4ade80', fontSize: '0.82rem' }}>✓ {f}</p>)}
          </div>
          <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid #7c3aed', borderRadius: 10, padding: 14 }}>
            <p style={{ color: '#a78bfa', fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>PRO</p>
            {['Tutto il FREE', 'Annunci AI', 'Analisi Mercato', 'Bozze + Editor', 'Chatbot AI H24', 'Estensione Chrome'].map(f => <p key={f} style={{ color: '#4ade80', fontSize: '0.82rem' }}>✓ {f}</p>)}
          </div>
        </div>
        <GoBtn to="/abbonamento" label="Vedi i piani →" />
      </>);
      case 'smartphone': return (<>
        <P>Puoi accedere alla Dashboard direttamente dal tuo smartphone — sia iPhone che Android. Ti consigliamo di aggiungerla alla schermata Home come se fosse un'app, cosi la trovi subito ogni volta che ne hai bisogno.</P>
        <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10, padding: 16, margin: '16px 0' }}>
          <p style={{ color: '#fbbf24', fontSize: '0.88rem', lineHeight: 1.6, margin: 0, fontWeight: 600 }}>⚠️ Importante — Per usare il Bot Vinted e l'Estensione Chrome devi usare un computer desktop. Su smartphone hai accesso a tutte le altre funzionalita: inventario, vendite, corso, annunci AI, analisi mercato, chatbot e bozze.</p>
        </div>

        <H3>🍎 iPhone — Aggiungi alla schermata Home</H3>
        <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 12, padding: isMobile ? 16 : 20, marginTop: 12 }}>
          <Step n={1}><strong style={{ color: '#e2e8f0' }}>Apri Safari</strong> — Apri l'app Safari sul tuo iPhone (non Chrome o altri browser — funziona solo con Safari per aggiungere alla Home).</Step>
          <Step n={2}><strong style={{ color: '#e2e8f0' }}>Vai sulla Dashboard</strong> — Nella barra degli indirizzi digita: <span style={{ color: '#a78bfa', fontFamily: 'JetBrains Mono,monospace', fontSize: '0.85rem' }}>dashboard.proelite.it</span> e premi Invio. Fai il login con Discord se non sei gia connesso.</Step>
          <Step n={3}><strong style={{ color: '#e2e8f0' }}>Tocca il tasto Condividi</strong> — In basso al centro dello schermo trovi il tasto Condividi (il quadrato con la freccia verso l'alto ↑). Toccalo.</Step>
          <Step n={4}><strong style={{ color: '#e2e8f0' }}>Aggiungi alla schermata Home</strong> — Scorri verso il basso nel menu che si apre e tocca "Aggiungi alla schermata Home". Poi tocca "Aggiungi" in alto a destra.</Step>
          <Step n={5}><strong style={{ color: '#e2e8f0' }}>Fatto!</strong> — Trovi l'icona Impero Elite sulla tua schermata Home. Aprila come una normale app — si apre a schermo intero senza la barra di Safari.</Step>
        </div>
        <div style={{ background: 'rgba(74,222,128,0.08)', borderLeft: '3px solid #4ade80', borderRadius: '0 8px 8px 0', padding: '12px 16px', margin: '12px 0', color: '#4ade80', fontSize: '0.88rem', lineHeight: 1.6 }}>
          💡 La Dashboard funziona anche offline per alcune sezioni grazie alla PWA. Per le funzionalita AI serve la connessione internet.
        </div>

        <H3>🤖 Android — Aggiungi alla schermata Home</H3>
        <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 12, padding: isMobile ? 16 : 20, marginTop: 12 }}>
          <Step n={1}><strong style={{ color: '#e2e8f0' }}>Apri Chrome</strong> — Apri l'app Google Chrome sul tuo Android (funziona anche con Samsung Internet o altri browser Chromium).</Step>
          <Step n={2}><strong style={{ color: '#e2e8f0' }}>Vai sulla Dashboard</strong> — Nella barra degli indirizzi digita: <span style={{ color: '#a78bfa', fontFamily: 'JetBrains Mono,monospace', fontSize: '0.85rem' }}>dashboard.proelite.it</span> e premi Invio. Fai il login con Discord se non sei gia connesso.</Step>
          <Step n={3}><strong style={{ color: '#e2e8f0' }}>Apri il menu</strong> — Tocca i 3 puntini ⋮ in alto a destra dello schermo per aprire il menu di Chrome.</Step>
          <Step n={4}><strong style={{ color: '#e2e8f0' }}>Aggiungi alla schermata Home</strong> — Nel menu tocca "Aggiungi alla schermata Home" (o "Installa app" se appare il banner in basso). Conferma toccando "Aggiungi".</Step>
          <Step n={5}><strong style={{ color: '#e2e8f0' }}>Fatto!</strong> — Trovi l'icona Impero Elite sulla tua schermata Home. Si apre come una normale app a schermo intero.</Step>
        </div>
        <div style={{ background: 'rgba(74,222,128,0.08)', borderLeft: '3px solid #4ade80', borderRadius: '0 8px 8px 0', padding: '12px 16px', margin: '12px 0', color: '#4ade80', fontSize: '0.88rem', lineHeight: 1.6 }}>
          💡 Su Android puoi anche scattare foto direttamente dall'app per aggiungerle all'inventario — tocca il tasto fotocamera nell'inventario.
        </div>

        <H3>📊 Cosa puoi fare da smartphone vs PC</H3>
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#2d1b69' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: '#e2e8f0', fontWeight: 600, borderBottom: '1px solid #2d1b69' }}>Funzionalita</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', color: '#e2e8f0', fontWeight: 600, borderBottom: '1px solid #2d1b69' }}>📱 Smartphone</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', color: '#e2e8f0', fontWeight: 600, borderBottom: '1px solid #2d1b69' }}>💻 PC</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Dashboard e statistiche', true, true],
                ['Inventario prodotti', true, true],
                ['Foto con fotocamera', true, false],
                ['Vendite e profitti', true, true],
                ['Corso reselling', true, true],
                ['Fornitori', true, true],
                ['Annunci AI', true, true],
                ['Analisi Mercato', true, true],
                ['Bozze', true, true],
                ['Chatbot AI H24', true, true],
                ['Bot Vinted (Estensione Chrome)', false, true],
                ['Pubblicazione eBay', true, true],
                ['Editor foto avanzato', 'base', true],
              ].map(([feat, phone, pc], i) => (
                <tr key={feat} style={{ background: i % 2 === 0 ? '#0d0b1e' : '#1a1035' }}>
                  <td style={{ padding: '9px 14px', color: '#9ca3af', borderBottom: '1px solid #2d1b69' }}>{feat}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'center', borderBottom: '1px solid #2d1b69', color: phone === true ? '#4ade80' : phone === false ? '#f87171' : '#fbbf24', fontWeight: 600 }}>{phone === true ? '✅' : phone === false ? '❌' : '✅ (base)'}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'center', borderBottom: '1px solid #2d1b69', color: pc === true ? '#4ade80' : pc === false ? '#f87171' : '#fbbf24', fontWeight: 600 }}>{pc === true ? '✅' : pc === false ? '❌' : '✅'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>);
      case 'inventario': return (<>
        <H3>Perche usare l'inventario</H3>
        <P>Senza un inventario digitale non sai cosa hai in casa, quanto vale, cosa hai gia venduto. Con l'inventario hai tutto sotto controllo in tempo reale: valore totale, prodotti disponibili, margini di profitto.</P>
        <H3>Come aggiungere un prodotto</H3>
        <Step n={1}>Clicca "+ Aggiungi" in alto a destra nella pagina Inventario</Step>
        <Step n={2}>Scegli "Manuale" per compilare tu, o "Genera con AI" (PRO) per far fare tutto all'AI</Step>
        <Step n={3}>Inserisci nome, categoria, prezzo acquisto e prezzo vendita</Step>
        <Step n={4}>Carica almeno 3 foto (fronte, retro, dettaglio)</Step>
        <Step n={5}>Clicca "Salva" — il prodotto appare nel tuo inventario</Step>
        <Tip>💡 Con "Genera con AI" basta caricare la foto: l'AI riconosce il prodotto, suggerisce nome, categoria e prezzo di mercato. Risparmi 5 minuti per prodotto.</Tip>
        <H3>Consigli per le foto</H3>
        <P>Sfondo bianco o neutro, luce naturale, almeno 3 foto per prodotto (fronte, retro, dettaglio difetti). Le foto buone fanno vendere di piu. Non usare flash diretto — crea ombre dure.</P>
        <Warn>⚠️ Foto scure o sfocate riducono le vendite del 40%. Investi 2 minuti in piu per fare foto decenti.</Warn>
        <GoBtn to="/products" label="Vai all'Inventario →" />
      </>);
      case 'vendite': return (<>
        <H3>Perche registrare ogni vendita</H3>
        <P>Senza dati non puoi sapere quanto stai guadagnando davvero, quali prodotti rendono di piu, se il tuo business sta crescendo. I dati sono il tuo vantaggio competitivo.</P>
        <H3>Come registrare una vendita</H3>
        <Step n={1}>Vai in Inventario e clicca sul prodotto venduto</Step>
        <Step n={2}>Clicca "Segna come venduto"</Step>
        <Step n={3}>Inserisci il prezzo di vendita finale e la piattaforma (Vinted/eBay)</Step>
        <Step n={4}>Conferma — il prodotto passa in "Venduto" e appare nelle statistiche</Step>
        <H3>Come leggere le statistiche</H3>
        <P>Totale venduto = quanto hai incassato. Profitto netto = vendita - acquisto - commissioni. Margine medio = quanto guadagni in % su ogni vendita. Usa questi dati per capire cosa comprare di piu.</P>
        <Tip>💡 Controlla il margine medio ogni settimana. Se scende sotto il 30%, stai comprando troppo caro o vendendo troppo basso.</Tip>
        <GoBtn to="/vendite" label="Vai alle Vendite →" />
      </>);
      case 'corso': return (<>
        <H3>Come e strutturato il corso</H3>
        <P>4 moduli progressivi: Modulo 1 (basi e mindset), Modulo 2 (trovare prodotti giusti), Modulo 3 (creare annunci che vendono), Modulo 4 (scalare e automatizzare). Ogni modulo si sblocca completando il precedente.</P>
        <H3>Come studiare in modo efficace</H3>
        <P>Non skippare le lezioni. Applica subito quello che impari. Il corso vale solo se metti in pratica. Studia 1 lezione al giorno e applica prima di andare alla successiva.</P>
        <Tip>💡 Chi completa il corso entro le prime 2 settimane ha il 3x di probabilita di fare la prima vendita rispetto a chi lo fa in un mese.</Tip>
        <GoBtn to="/corso" label="Vai al Corso →" />
      </>);
      case 'fornitori': return (<>
        <H3>Cos'e la lista fornitori</H3>
        <P>Contatti esclusivi selezionati da Willy. Fornitori verificati con cui puoi ordinare a prezzi all'ingrosso per rivendere con margine.</P>
        <H3>Come usare la lista</H3>
        <P>Leggi categoria e descrizione fornitore. Contatta via WhatsApp con messaggio professionale. Non chiedere prezzi generici ma specifica cosa cerchi.</P>
        <H3>Consigli per il primo contatto</H3>
        <P>Presentati come rivenditore professionale. Chiedi listino prezzi e MOQ (quantita minima ordine). Non fare domande banali gia nella lista.</P>
        <Warn>⚠️ Non condividere i contatti dei fornitori con persone fuori dalla community. Sono esclusivi per i membri Impero Elite.</Warn>
        <GoBtn to="/fornitori" label="Vai ai Fornitori →" />
      </>);
      case 'annunci': return (<>
        <H3>Perche usare l'AI per gli annunci</H3>
        <P>Scrivere un buon annuncio richiede tempo e esperienza. L'AI genera titolo ottimizzato, descrizione persuasiva e hashtag in 10 secondi. Piu tempo risparmi, piu prodotti riesci a pubblicare.</P>
        <H3>Come generare un annuncio</H3>
        <Step n={1}>Vai in Annunci → Crea annuncio</Step>
        <Step n={2}>Seleziona il prodotto dall'inventario</Step>
        <Step n={3}>Scegli la piattaforma (Vinted/eBay)</Step>
        <Step n={4}>Clicca "Genera" — l'AI crea titolo, descrizione e hashtag</Step>
        <Step n={5}>Modifica se vuoi, poi salva come bozza</Step>
        <H3>Titolo: le regole d'oro</H3>
        <P>Max 60 caratteri su Vinted. Includi: brand + tipo prodotto + taglia + condizione. Esempio: "Nike Air Max 90 taglia 42 — ottime condizioni".</P>
        <Tip>💡 L'AI da un ottimo punto di partenza ma tu conosci il prodotto meglio. Aggiungi dettagli specifici (difetti, misure) per aumentare la fiducia.</Tip>
        <GoBtn to="/annunci/crea" label="Crea un Annuncio →" />
      </>);
      case 'analisi': return (<>
        <H3>Perche analizzare il mercato prima di vendere</H3>
        <P>Senza analisi potresti vendere troppo basso (perdi margine) o troppo alto (nessuno compra). L'analisi ti dice esattamente il prezzo giusto in tempo reale cercando su eBay, Vinted e Google Shopping.</P>
        <H3>Come fare una ricerca</H3>
        <Step n={1}>Vai in Analisi Mercato</Step>
        <Step n={2}>Inserisci nome prodotto e/o carica la foto</Step>
        <Step n={3}>Clicca "Analizza" — la Dashboard cerca i prezzi reali su tutte le piattaforme</Step>
        <Step n={4}>Leggi lo score di vendibilita (1-10) e il prezzo consigliato</Step>
        <H3>Come usare i dati per fissare il prezzo</H3>
        <P>Formula: prezzo medio venduto -10% per vendere veloce, prezzo medio per velocita normale, +10% se condizioni eccellenti o accessori inclusi.</P>
        <Tip>💡 I risultati vengono salvati per 6 ore. Se il mercato cambia rapidamente, puoi forzare una nuova ricerca.</Tip>
        <GoBtn to="/analisi-mercato" label="Analizza un Prodotto →" />
      </>);
      case 'bozze': return (<>
        <H3>Cos'e una bozza</H3>
        <P>Una bozza e un annuncio pronto da pubblicare, con foto ottimizzate, titolo e descrizione gia scritti. Puoi crearne quante vuoi e pubblicarle quando vuoi.</P>
        <H3>Come usare l'editor foto</H3>
        <P>Luminosita: aumenta se la foto e scura. Contrasto: per far risaltare i dettagli. Saturazione: leggermente per colori vivaci. Zoom/Ritaglia: elimina sfondi. Cornici: aggiungi look professionale.</P>
        <Tip>💡 Usa "Applica a tutte" per applicare le stesse impostazioni a tutte le foto della bozza in un click.</Tip>
        <H3>Come pubblicare su Vinted</H3>
        <P>Serve l'estensione Chrome installata. Clicca "Pubblica su Vinted" → apri Vinted → l'estensione compila automaticamente tutti i campi → controlla e conferma.</P>
        <GoBtn to="/bozze" label="Vai alle Bozze →" />
      </>);
      case 'chatbot': return (<>
        <H3>Cosa puo fare il chatbot</H3>
        <P>Risponde a qualsiasi domanda su: come usare la Dashboard, strategie di reselling, come scrivere annunci, gestire resi e problemi con acquirenti, consigli su fotografia prodotti. Disponibile 24/7.</P>
        <H3>Esempi di domande utili</H3>
        <P>"Come faccio a vendere piu velocemente su Vinted?" / "Qual e il prezzo giusto per una felpa Ralph Lauren usata?" / "Come rispondo a un acquirente che vuole uno sconto?"</P>
        <H3>Limite giornaliero</H3>
        <P>30 messaggi al giorno. Il contatore si azzera ogni mezzanotte. Per problemi tecnici o supporto umano, clicca "Apri ticket".</P>
        <Warn>⚠️ Il chatbot e un'AI — verifica sempre le informazioni importanti. Per problemi tecnici con la Dashboard, apri un ticket di supporto.</Warn>
      </>);
      case 'estensione': return (<>
        <H3>Cos'e e perche serve</H3>
        <P>L'estensione Chrome e il ponte tra la Dashboard e Vinted. Senza di essa non puoi pubblicare automaticamente. Funziona solo su desktop con Chrome.</P>
        <H3>Come installarla</H3>
        <Step n={1}>Vai su Chrome e apri chrome://extensions</Step>
        <Step n={2}>Attiva "Modalita sviluppatore" in alto a destra</Step>
        <Step n={3}>Clicca "Carica estensione non pacchettizzata"</Step>
        <Step n={4}>Seleziona la cartella dell'estensione fornita</Step>
        <H3>Consigli per non ricevere ban</H3>
        <P>Non superare 30 messaggi al giorno. Non pubblicare piu di 10-15 annunci consecutivi senza pausa. Usa l'estensione in modo naturale.</P>
        <Warn>⚠️ Vinted puo limitare il tuo account se rileva attivita sospetta. Rispetta i limiti giornalieri.</Warn>
      </>);
      default: return null;
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 1100, margin: '0 auto', paddingTop: isMobile ? 68 : 32, display: 'flex', gap: 24 }}>

        {/* Sidebar indice — solo desktop */}
        {!isMobile && (
          <div style={{ width: 200, flexShrink: 0, position: 'sticky', top: 80, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
            <p style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 12 }}>Indice</p>
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)} style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '7px 10px', borderRadius: 8,
                background: activeSection === s.id ? 'rgba(124,58,237,0.12)' : 'transparent',
                border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: activeSection === s.id ? 600 : 400,
                color: activeSection === s.id ? '#a78bfa' : '#9ca3af', textAlign: 'left', transition: '0.15s',
              }}>
                <span style={{ fontSize: '0.75rem' }}>{s.title.split(' ')[0]}</span>
                <span>{s.title.split(' ').slice(1).join(' ')}</span>
                {s.pro && <span style={{ marginLeft: 'auto', background: 'rgba(124,58,237,0.2)', color: '#a78bfa', fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: 4 }}>PRO</span>}
              </button>
            ))}
          </div>
        )}

        {/* Contenuto principale */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontFamily: 'Syne,sans-serif', fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 800, color: '#e2e8f0', marginBottom: 4 }}>📖 Guida Completa</h1>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: 20 }}>Tutto quello che ti serve per usare la Dashboard al massimo</p>

          {/* Ricerca */}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca nelle guide..." style={{
            width: '100%', padding: '10px 16px', background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 10,
            color: '#e2e8f0', fontSize: '0.88rem', outline: 'none', marginBottom: 24, boxSizing: 'border-box',
          }} />

          {/* Sezioni */}
          {filtered.map(s => (
            <div key={s.id} ref={el => sectionRefs.current[s.id] = el} id={s.id} style={{
              background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 14, padding: isMobile ? 20 : 32, marginBottom: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <h2 style={{ color: '#a78bfa', fontSize: '1.15rem', fontWeight: 700, fontFamily: 'Syne,sans-serif', margin: 0 }}>{s.id === 'video' ? '📺 Tutorial Completo — Dashboard & Estensione' : s.title}</h2>
                {s.pro && <span style={{ background: 'rgba(124,58,237,0.2)', color: '#a78bfa', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 9999 }}>PRO</span>}
                {s.badge && <span style={{ marginLeft: 'auto', background: 'rgba(124,58,237,0.15)', color: '#a78bfa', fontSize: '0.65rem', fontWeight: 700, padding: '3px 10px', borderRadius: 9999, border: '1px solid rgba(124,58,237,0.3)' }}>💎 CONTENUTO PRO</span>}
              </div>
              {renderSection(s.id)}
            </div>
          ))}
        </div>
      </div>

      {/* Bottone torna su */}
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{
          position: 'fixed', bottom: 80, right: 24, width: 40, height: 40, borderRadius: '50%',
          background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: 'none', color: 'white',
          fontSize: '1rem', cursor: 'pointer', boxShadow: '0 4px 16px rgba(124,58,237,0.4)', zIndex: 50,
        }}>↑</button>
      )}
    </Layout>
  );
}
